import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { tenantApi } from '../api/tenantApi';

// ---------------------------------------------------------------------------
// Toast system — replaces MUI Snackbar with a terminal-log-style stack so it
// matches the rest of the panel. Wrap <AdminLayout> (or App) in <ToastProvider>.
// ---------------------------------------------------------------------------
const ToastCtx = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const push = useCallback((msg, type = 'success', title) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, msg, type, title }]);
    setTimeout(() => {
      setToasts((t) => t.filter((x) => x.id !== id));
    }, 4200);
  }, []);

  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toast-stack">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast--${t.type}`}>
            <div className="toast__title">
              {t.title || (t.type === 'error' ? '// ERROR' : '// OK')}
            </div>
            {t.msg}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const push = useContext(ToastCtx);
  return {
    success: (msg, title) => push(msg, 'success', title),
    error: (msg, title) => push(msg, 'error', title || '// ERROR'),
  };
}

export function errText(err, fallback = 'Request failed. Check connection and try again.') {
  return err?.message || fallback;
}

// ---------------------------------------------------------------------------
// Panel — the "terminal window" wrapper used on every page
// ---------------------------------------------------------------------------
export function Panel({ title, right, children }) {
  return (
    <div className="term-panel">
      <div className="term-panel__head">
        <div className="term-panel__head-left">
          <span className="term-dots"><span className="d1" /><span className="d2" /><span className="d3" /></span>
          <span className="term-panel__title">{title}</span>
        </div>
        {right}
      </div>
      <div className="term-panel__body">{children}</div>
    </div>
  );
}

export function Loader({ label = 'loading' }) {
  return <div className="loader-line">{label}</div>;
}

export function EmptyState({ children }) {
  return <div className="empty-state">{children}</div>;
}

// ---------------------------------------------------------------------------
// TenantSelect — searchable tenant dropdown, replacing free-text clientCode
// entry on lookup pages (Admin Credentials, Permissions, Organization
// Profile, Seed Management, ...). Backed by the SAME list endpoint as the
// Tenant List page (tenantApi.getAllTenants()) so it always reflects what's
// actually registered in the Master DB, filtered locally by an in-dropdown
// search box (clientCode / clientName / emailId).
// ---------------------------------------------------------------------------
export function TenantSelect({ value, onSelect, placeholder = 'select tenant...' }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState(null); // null = not fetched yet
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  const ensureLoaded = useCallback(async () => {
    if (rows !== null || loading) return;
    setLoading(true);
    try {
      const res = await tenantApi.getAllTenants();
      setRows(res.data || []);
    } catch (err) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [rows, loading]);

  const openDropdown = () => {
    setOpen(true);
    setQ('');
    ensureLoaded();
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = (rows || []).filter((r) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (
      (r.clientCode || '').toLowerCase().includes(s) ||
      (r.clientName || '').toLowerCase().includes(s) ||
      (r.emailId || '').toLowerCase().includes(s)
    );
  });

  const handlePick = (row) => {
    setOpen(false);
    onSelect(row.clientCode, row);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: 260 }}>
      <button
        type="button"
        className="term-select"
        style={{ width: '100%', textAlign: 'left', cursor: 'pointer' }}
        onClick={() => (open ? setOpen(false) : openDropdown())}
      >
        {value || <span style={{ color: 'var(--text-dim)' }}>{placeholder}</span>}
      </button>

      {open && (
        <div className="tenant-select__panel">
          <input
            autoFocus
            className="term-input"
            style={{ width: '100%', marginBottom: 8 }}
            placeholder="search clientCode / name / email..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="tenant-select__list">
            {loading && <div className="loader-line">fetching tenants</div>}
            {!loading && rows !== null && filtered.length === 0 && (
              <div className="empty-state" style={{ padding: '8px 4px' }}>No tenants match.</div>
            )}
            {!loading && filtered.map((r) => (
              <div key={r.clientCode} className="tenant-select__opt" onClick={() => handlePick(r)}>
                <b style={{ color: 'var(--green)' }}>{r.clientCode}</b>
                {r.clientName && <span style={{ color: 'var(--text-dim)' }}> — {r.clientName}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal — generic overlay dialog for forms (create/edit), replacing the old
// inline-at-the-bottom Panel pattern. While `closable` is false (e.g. a save
// request is in flight) there is no close (×) button, clicking the backdrop
// does nothing, and Escape is ignored — the only way out is for the caller
// to flip `closable` back to true once the request settles.
// ---------------------------------------------------------------------------
export function Modal({ title, children, closable = true, onClose, width = 560 }) {
  useEffect(() => {
    if (!closable) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [closable, onClose]);

  return (
    <div
      className="term-modal-overlay"
      onMouseDown={(e) => { if (closable && e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="term-panel term-modal" style={{ width, maxWidth: '92vw' }}>
        <div className="term-panel__head">
          <div className="term-panel__head-left">
            <span className="term-dots"><span className="d1" /><span className="d2" /><span className="d3" /></span>
            <span className="term-panel__title">{title}</span>
          </div>
          {closable && (
            <button type="button" className="term-modal__close" onClick={onClose} aria-label="close">✕</button>
          )}
        </div>
        <div className="term-panel__body term-modal__body">{children}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pagination — "rows per page" selector + page nav, themed to match the rest
// of the panel. Purely presentational: the caller owns page/pageSize state
// and slices a local array (or re-queries a paged API) accordingly.
// ---------------------------------------------------------------------------
export function Pagination({
  page,
  totalPages,
  pageSize,
  pageSizeOptions = [10, 25, 50, 100],
  onPageChange,
  onPageSizeChange,
}) {
  return (
    <div className="term-pagination">
      <fieldset className="term-fieldset">
        <legend>rows_per_page</legend>
        <select
          className="term-fieldset__select"
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
        >
          {pageSizeOptions.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </fieldset>

      <div className="term-pagination__nav">
        <span className="term-pagination__info">page {page} of {totalPages || 1}</span>
        <button type="button" className="term-btn term-btn--sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>prev</button>
        <button type="button" className="term-btn term-btn--sm" disabled={page >= (totalPages || 1)} onClick={() => onPageChange(page + 1)}>next</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Confirm — lightweight in-panel confirm (no browser confirm(), stays on-theme)
// ---------------------------------------------------------------------------
export function useConfirm() {
  const [state, setState] = useState(null); // { message, resolve }

  const confirm = useCallback((message) => {
    return new Promise((resolve) => {
      setState({ message, resolve });
    });
  }, []);

  const node = state ? (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000,
      }}
    >
      <div className="term-panel" style={{ width: 380, margin: 0 }}>
        <div className="term-panel__head">
          <div className="term-panel__head-left">
            <span className="term-dots"><span className="d1" /><span className="d2" /><span className="d3" /></span>
            <span className="term-panel__title">// CONFIRM ACTION</span>
          </div>
        </div>
        <div className="term-panel__body">
          <p style={{ fontSize: 12.5, marginBottom: 18 }}>{state.message}</p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              className="term-btn term-btn--ghost"
              onClick={() => { state.resolve(false); setState(null); }}
            >
              cancel
            </button>
            <button
              className="term-btn term-btn--danger"
              onClick={() => { state.resolve(true); setState(null); }}
            >
              confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  return [confirm, node];
}

// ---------------------------------------------------------------------------
// CaptchaConfirm — for irreversible actions (drop database, remove tenant
// registration, clear seed/reference data). The user must retype a random
// code exactly before "confirm" is enabled; plain OK/Cancel isn't enough
// for anything that deletes or drops data.
// ---------------------------------------------------------------------------
function randomCode(len = 6) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no 0/O/1/I ambiguity
  let out = '';
  for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

export function useCaptchaConfirm() {
  const [state, setState] = useState(null); // { message, code, input, resolve }

  const confirm = useCallback((message) => {
    return new Promise((resolve) => {
      setState({ message, code: randomCode(), input: '', resolve });
    });
  }, []);

  const setInput = (v) => setState((s) => (s ? { ...s, input: v } : s));
  const cancel = () => { state.resolve(false); setState(null); };
  const doConfirm = () => {
    if (state.input.trim().toUpperCase() !== state.code) return;
    state.resolve(true);
    setState(null);
  };

  const node = state ? (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000,
      }}
    >
      <div className="term-panel" style={{ width: 420, margin: 0 }}>
        <div className="term-panel__head">
          <div className="term-panel__head-left">
            <span className="term-dots"><span className="d1" /><span className="d2" /><span className="d3" /></span>
            <span className="term-panel__title">// DESTRUCTIVE ACTION — VERIFY TO CONTINUE</span>
          </div>
        </div>
        <div className="term-panel__body">
          <p style={{ fontSize: 12.5, marginBottom: 14, color: 'var(--red, #e5484d)' }}>{state.message}</p>
          <p style={{ fontSize: 12, marginBottom: 8 }}>
            Type <b style={{ letterSpacing: '0.2em', color: 'var(--green)' }}>{state.code}</b> below to confirm:
          </p>
          <input
            autoFocus
            className="term-input"
            style={{ width: '100%', marginBottom: 16, letterSpacing: '0.2em', textTransform: 'uppercase' }}
            value={state.input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && doConfirm()}
            placeholder="enter code"
          />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="term-btn term-btn--ghost" onClick={cancel}>cancel</button>
            <button
              className="term-btn term-btn--danger"
              disabled={state.input.trim().toUpperCase() !== state.code}
              onClick={doConfirm}
            >
              confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  return [confirm, node];
}